import React, { useState, useEffect } from "react";
import { Select, InputNumber } from "antd";
import FilterFooter from "./FilterFooter";
import type { NumericCondition } from "./filterTypes";
import { conditions } from "./filtersUtil";

type FilterConditionSelectorProps = {
  condition?: NumericCondition | null;
  value?: number | null;
  minValue?: any | null;
  maxValue?: any | null;
  onConditionChange: (condition: NumericCondition | null) => void;
  onValueChange: (value: number | null) => void;
  onClose: () => void;
  onClear?: () => void;
  onApply?: (
    condition: NumericCondition | null,
    value: number | { min: number | null; max: number | null },
  ) => void;
  onDelete?: () => void;
  placeholder?: string;
  style?: React.CSSProperties;
  initialCondition?: NumericCondition | null;
  initialValue?: number | null;
  isActiveFilter?: boolean;
  isDropdown?: boolean;
  hideFooter?: boolean;
  useAlternativeLayout?: boolean;
  disabled?: boolean;
};

const FilterConditionSelector = ({
  condition: externalCondition,
  value: externalValue,
  minValue: externalMinValue,
  maxValue: externalMaxValue,
  onConditionChange,
  onValueChange,
  onClose,
  onApply,
  onDelete,
  placeholder = "Choose a condition",
  style = { width: "100%" },
  initialCondition = null,
  initialValue = null,
  isActiveFilter = false,
  isDropdown = false,
  hideFooter = false,
  useAlternativeLayout = false,
  disabled = false,
}: FilterConditionSelectorProps) => {
  const [condition, setCondition] = useState(
    externalCondition || initialCondition,
  );
  const [value, setValue] = useState(externalValue || initialValue);
  const [minValue, setMinValue] = useState(externalMinValue || null);
  const [maxValue, setMaxValue] = useState(externalMaxValue || null);

  useEffect(() => {
    if (isActiveFilter && initialCondition) {
      onConditionChange(initialCondition);
      if (initialValue !== null && initialValue !== undefined) {
        onValueChange(initialValue);
      }
    }
  }, [
    isActiveFilter,
    initialCondition,
    initialValue,
    onConditionChange,
    onValueChange,
  ]);

  useEffect(() => {
    if (externalCondition !== undefined) {
      setCondition(externalCondition);
    }
  }, [externalCondition]);

  useEffect(() => {
    if (externalValue !== undefined) {
      setValue(externalValue);
    }
  }, [externalValue]);

  useEffect(() => {
    if (externalMinValue !== undefined) {
      setMinValue(externalMinValue);
    }
  }, [externalMinValue]);

  useEffect(() => {
    if (externalMaxValue !== undefined) {
      setMaxValue(externalMaxValue);
    }
  }, [externalMaxValue]);

  const selectedCondition = conditions.find((c) => c.value === condition);
  const showValueInput = selectedCondition && selectedCondition.needsValue;

  const isApplyDisabled =
    !condition ||
    (condition === "between" &&
      (minValue === null ||
        maxValue === null ||
        minValue === "" ||
        maxValue === "")) ||
    (showValueInput && condition !== "between" && value === null);

  const handleConditionChange = (newCondition: NumericCondition) => {
    setCondition(newCondition);

    if (newCondition === "between") {
      setMinValue(null);
      setMaxValue(null);
    } else {
      setValue(null);
    }

    if (onConditionChange) {
      onConditionChange(newCondition);
    }
  };

  const handleValueChange = (newValue: number | null) => {
    setValue(newValue);

    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const handleMinValueChange = (newMinValue: number | null) => {
    setMinValue(newMinValue);
  };

  const handleMaxValueChange = (newMaxValue: number | null) => {
    setMaxValue(newMaxValue);
  };

  const handleClear = () => {
    setCondition(null);
    setValue(null);
    setMinValue(null);
    setMaxValue(null);
  };

  const handleApply = () => {
    if (onApply) {
      if (condition === "between") {
        onApply(condition, { min: minValue, max: maxValue });
      } else {
        onApply(condition, value!);
      }
    }
  };

  return (
    <div className="flex flex-col w-full h-full min-h-[250px]">
      <div className="flex flex-col flex-1 gap-2">
        <p className="mt-2">Choose a condition</p>
        <Select
          data-testid="filter-condition-select"
          value={condition}
          onChange={handleConditionChange}
          placeholder={placeholder}
          style={style}
          showSearch={{
            filterOption: (input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
          }}
          options={conditions.map((conditionOption) => ({
            value: conditionOption.value,
            label: conditionOption.label,
          }))}
          disabled={disabled}
          className="filter-select"
        />

        {showValueInput && condition === "between" && (
          <>
            <p className="mt-3">Enter minimum value</p>
            <InputNumber
              value={minValue}
              onChange={handleMinValueChange}
              placeholder="Min value"
              style={style}
              className="rounded-lg"
              disabled={disabled}
              data-testid="filter-min-value-input"
            />
            <p className="mt-3">Enter maximum value</p>
            <InputNumber
              value={maxValue}
              onChange={handleMaxValueChange}
              placeholder="Max value"
              style={style}
              className="rounded-lg"
              disabled={disabled}
              data-testid="filter-max-value-input"
            />
          </>
        )}

        {showValueInput && condition !== "between" && (
          <>
            <p className="mt-3">Enter a value</p>
            <InputNumber
              value={value}
              onChange={handleValueChange}
              placeholder="Enter value"
              style={style}
              className="rounded-lg"
              disabled={disabled}
              data-testid="filter-value-input"
            />
          </>
        )}
      </div>
      {!hideFooter && (
        <FilterFooter
          onClose={onClose}
          onClear={handleClear}
          onApply={handleApply}
          onDelete={onDelete}
          isApplyDisabled={isApplyDisabled}
          isDropdown={isDropdown}
          useAlternativeLayout={useAlternativeLayout}
        />
      )}
    </div>
  );
};

export default FilterConditionSelector;
